import { useEffect, useRef } from 'react';

export default function RelatedRegulationArticleModal({ documentTitle, articleLabel, articleTitle, parentRef, onClose, children }) {
  const modalRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const trigger = document.activeElement;
    const parent = parentRef.current;
    const wasInert = parent?.inert;
    const previousAriaHidden = parent?.getAttribute('aria-hidden');
    closeRef.current?.focus({ preventScroll: true });
    if (parent) {
      parent.inert = true;
      parent.setAttribute('aria-hidden', 'true');
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        onCloseRef.current();
      } else if (event.key === 'Tab') {
        const focusable = Array.from(modalRef.current.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), [tabindex="0"]'
        ));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      if (parent) {
        parent.inert = wasInert;
        if (previousAriaHidden === null) parent.removeAttribute('aria-hidden');
        else parent.setAttribute('aria-hidden', previousAriaHidden);
      }
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [parentRef]);

  return (
    <div className="regulatoryReaderOverlay regulatoryRelatedArticleOverlay" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section ref={modalRef} className="regulatoryReaderModal regulatoryRelatedArticleModal"
        role="dialog" aria-modal="true" aria-labelledby="related-regulation-title">
        <header className="regulatoryReaderHeader">
          <div className="regulatoryReaderTitleBlock">
            <h2 id="related-regulation-title">{documentTitle}</h2>
          </div>
          <button ref={closeRef} type="button" className="regulatoryReaderClose" onClick={onClose}
            aria-label="Close corresponding article" title="Close">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </header>
        <div className="regulatoryRelatedArticleContent" tabIndex={0} role="region" aria-label={`${articleLabel} text`}>
          <article className="regulatoryReaderSection">
            <div className="regulatoryReaderSectionTop">
              <div>
                <p className="regulatoryReaderSectionLabel">{articleLabel}</p>
                <h3>{articleTitle}</h3>
              </div>
            </div>
            <div className="regulatoryReaderArticleBody">{children}</div>
          </article>
        </div>
      </section>
    </div>
  );
}
